import ActiviteIcon from "src/core/assets/images/radar/activity.svg?react";
import ClientIcon from "src/core/assets/images/radar/client.svg?react";
import LocalisationIcon from "src/core/assets/images/radar/location.svg?react";
import MissionsIcon from "src/core/assets/images/radar/mission.svg?react";
import { Status } from "src/core/constants/folders";
import { CustomRiskAxis, CustomRiskLevel } from "src/core/constants/risk";
import { IRiskDisplay } from "src/core/interfaces/Folders";
import { cn } from "src/core/lib/utils";
import { getRiskClass } from "src/core/utils/riskClass";

const RiskAxisBadge = ({
  axis,
  icon,
}: {
  axis: { axisName: string; riskLevel: string };
  icon: React.ReactNode;
}) => {
  return (
    <div className="relative group inline-flex">
      <div className={`${axis.riskLevel.toLowerCase()}-risk cursor-pointer`}>{icon}</div>
      <div
        className={cn(
          "pointer-events-none",
          "opacity-0 group-hover:opacity-100 transition-opacity duration-150",
          "flex items-center h-20 absolute bottom-[15px] left-[-10px]",
          "px-3 py-2 rounded-lg text-white font-medium text-sm whitespace-nowrap z-10",
          getRiskClass("dot", axis.riskLevel as CustomRiskLevel)
        )}
      >
        <div className="text-centerfont-semibold">{axis.axisName}</div>
      </div>
    </div>
  );
};

export const renderValidationPendingHRRisk = (
  riskDisplay: IRiskDisplay | undefined,
  t: (k: string) => string
) => {
  if (!riskDisplay) return "-";

  const { axes } = riskDisplay;
  const riskSet = new Set<string>();

  for (const axis of axes) {
    if (axis.axisName === CustomRiskAxis.CLIENT) {
      axis?.risks?.forEach((risk) => {
        if (risk === CustomRiskAxis.SANCTION || risk === CustomRiskAxis.PEP) {
          riskSet.add(risk);
        }
      });
    }
    if (axis.axisName === CustomRiskAxis.LOCALISATION) {
      riskSet.add(CustomRiskAxis.LOCALISATION);
    }
  }

  const displayOrder = [CustomRiskAxis.SANCTION, CustomRiskAxis.LOCALISATION, CustomRiskAxis.PEP];

  const getBadgeStyle = (risk: string) => {
    switch (risk) {
      case CustomRiskAxis.SANCTION:
        return "bg-[#FFE4E4] text-[#A62108]";
      case CustomRiskAxis.LOCALISATION:
        return "bg-[#FFEDE7] text-[#E22D05]";
      case CustomRiskAxis.PEP:
        return "bg-[#FDF0E8] text-[#F3650C]";
    }
  };

  const risksToDisplay = displayOrder.filter((r) => riskSet.has(r));

  return risksToDisplay.length > 0 ? (
    <div className="flex flex-wrap gap-2">
      {risksToDisplay.map((risk) => (
        <span
          key={risk}
          className={`inline-block rounded-lg px-3 py-1 text-sm font-semibold ${getBadgeStyle(
            risk
          )}`}
        >
          {t(`folders.list.columns.riskLevels.${risk.toLowerCase()}`)}
        </span>
      ))}
    </div>
  ) : (
    "-"
  );
};

export const renderRiskIconsAxis = (riskDisplay: IRiskDisplay | undefined) => {
  if (!riskDisplay) return "-";

  const getAxisIcon = (axisName: string) => {
    switch (axisName) {
      case CustomRiskAxis.CLIENT:
        return <ClientIcon />;
      case CustomRiskAxis.LOCALISATION:
        return <LocalisationIcon />;
      case CustomRiskAxis.MISSIONS:
        return <MissionsIcon />;
      case CustomRiskAxis.ACTIVITE:
        return <ActiviteIcon />;
      default:
        return null;
    }
  };

  const axisOrder = [
    CustomRiskAxis.ACTIVITE,
    CustomRiskAxis.LOCALISATION,
    CustomRiskAxis.CLIENT,
    CustomRiskAxis.MISSIONS,
  ];

  const validAxes = riskDisplay.axes
    .filter((axis) => axisOrder.includes(axis.axisName as CustomRiskAxis))
    .sort((a, b) => {
      const indexA = axisOrder.indexOf(a.axisName as CustomRiskAxis);
      const indexB = axisOrder.indexOf(b.axisName as CustomRiskAxis);
      return indexA - indexB;
    });

  return (
    <div className="flex flex-row gap-x-10">
      {validAxes.map((axis) => (
        <RiskAxisBadge key={axis.axisName} axis={axis} icon={getAxisIcon(axis.axisName)} />
      ))}
    </div>
  );
};

export const renderRiskByStatus = (
  status: Status,
  riskDisplay: IRiskDisplay | undefined,
  t: (k: string) => string
) => {
  if (!riskDisplay) return "-";

  const level = riskDisplay.globalRiskLevel?.toLowerCase();
  if (!level) return "-";

  const levelLabel = t(`folders.list.columns.riskLevels.${level}`);

  switch (status) {
    case Status.DRAFT:
      return (
        <div className={`font-medium ${level}-risk`}>
          {levelLabel}
          <span className="font-light">&nbsp;:&nbsp;{t("folders.list.columns.inProgress")}</span>
        </div>
      );
    case Status.VALIDATION_PENDING:
    case Status.RENEWAL:
    case Status.VALIDATED:
      return <span className={`font-medium ${level}-risk`}>{levelLabel}</span>;
    case Status.VALIDATION_PENDING_HR:
      return renderValidationPendingHRRisk(riskDisplay, t);
    default:
      return "-";
  }
};
